import { ethers } from 'ethers';

// Polygon Mainnet Configuration
export const POLYGON_CONFIG = {
  chainId: 137,
  chainName: 'Polygon Mainnet',
  rpcUrl: 'https://polygon-rpc.com',
  blockExplorer: 'https://polygonscan.com',
  nativeCurrency: {
    name: 'MATIC',
    symbol: 'MATIC',
    decimals: 18
  }
};

// USDC Contract on Polygon
export const USDC_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
export const USDC_DECIMALS = 6;

// ERC20 ABI (minimal - just what we need)
export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)'
];

/**
 * Check if MetaMask is installed
 */
export const isMetaMaskInstalled = () => {
  return typeof window.ethereum !== 'undefined';
};

/**
 * Switch to Polygon network
 */
export const switchToPolygon = async () => {
  if (!isMetaMaskInstalled()) {
    throw new Error('MetaMask is not installed');
  }

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: `0x${POLYGON_CONFIG.chainId.toString(16)}` }],
    });
  } catch (switchError) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      try {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: `0x${POLYGON_CONFIG.chainId.toString(16)}`,
            chainName: POLYGON_CONFIG.chainName,
            rpcUrls: [POLYGON_CONFIG.rpcUrl],
            nativeCurrency: POLYGON_CONFIG.nativeCurrency,
            blockExplorerUrls: [POLYGON_CONFIG.blockExplorer]
          }]
        });
      } catch (addError) {
        throw new Error('Failed to add Polygon network to MetaMask');
      }
    } else {
      throw switchError;
    }
  }
};

/**
 * Get USDC balance for an address
 */
export const getUSDCBalance = async (address) => {
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const usdcContract = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, provider);
    const balance = await usdcContract.balanceOf(address);
    return ethers.utils.formatUnits(balance, USDC_DECIMALS);
  } catch (error) {
    console.error('Error getting USDC balance:', error);
    throw error;
  }
};

/**
 * Send USDC to a recipient
 */
export const sendUSDC = async (recipientAddress, amount) => {
  try {
    // Ensure we're on Polygon
    await switchToPolygon();

    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const usdcContract = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, signer);

    // Convert amount to proper decimals
    const amountInUnits = ethers.utils.parseUnits(amount.toString(), USDC_DECIMALS);

    // Send transaction
    const tx = await usdcContract.transfer(recipientAddress, amountInUnits);
    
    return {
      hash: tx.hash,
      transaction: tx
    };
  } catch (error) {
    console.error('Error sending USDC:', error);
    throw error;
  }
};

/**
 * Wait for transaction confirmation
 */
export const waitForTransaction = async (txHash, confirmations = 1) => {
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const receipt = await provider.waitForTransaction(txHash, confirmations);
    return receipt;
  } catch (error) {
    console.error('Error waiting for transaction:', error);
    throw error;
  }
};

/**
 * Get transaction details
 */
export const getTransactionDetails = async (txHash) => {
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const tx = await provider.getTransaction(txHash);
    const receipt = await provider.getTransactionReceipt(txHash);
    
    return {
      transaction: tx,
      receipt: receipt,
      status: receipt ? receipt.status : null,
      confirmations: receipt ? receipt.confirmations : 0
    };
  } catch (error) {
    console.error('Error getting transaction details:', error);
    throw error;
  }
};

/**
 * Format address for display (0x1234...5678)
 */
export const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * Get Polygonscan link for transaction
 */
export const getPolygonscanLink = (txHash) => {
  return `${POLYGON_CONFIG.blockExplorer}/tx/${txHash}`;
};

/**
 * Validate Ethereum address
 */
export const isValidAddress = (address) => {
  return ethers.utils.isAddress(address);
};

/**
 * Estimate gas for USDC transfer
 */
export const estimateTransferGas = async (recipientAddress, amount) => {
  try {
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    const signer = provider.getSigner();
    const usdcContract = new ethers.Contract(USDC_ADDRESS, ERC20_ABI, signer);
    
    const amountInUnits = ethers.utils.parseUnits(amount.toString(), USDC_DECIMALS);
    const gasEstimate = await usdcContract.estimateGas.transfer(recipientAddress, amountInUnits);
    
    // Get gas price
    const gasPrice = await provider.getGasPrice();
    
    // Calculate cost in MATIC
    const gasCostInMatic = ethers.utils.formatEther(gasEstimate.mul(gasPrice));
    
    return {
      gasLimit: gasEstimate.toString(),
      gasPrice: ethers.utils.formatUnits(gasPrice, 'gwei'),
      gasCostInMatic: gasCostInMatic,
      gasCostInUSD: (parseFloat(gasCostInMatic) * 0.5).toFixed(4) // Rough estimate at $0.50 per MATIC
    };
  } catch (error) {
    console.error('Error estimating gas:', error);
    return null;
  }
};
